import { forwardRef, useImperativeHandle, useLayoutEffect, useMemo, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { breadcrumb, displayTitle } from '@terrazzo/core'
import { searchTitles } from '../store/search'
import { store } from '../store/store'

export interface LinkPickerHandle {
  /** Returns true when the key was handled by the picker. */
  handleKey(e: KeyboardEvent): boolean
}

type Pos = { left: number; top?: number; bottom?: number; maxHeight: number }

/**
 * Where the picker goes: under the row being typed in, or above it when the keyboard leaves no
 * room below. Drawn over the page itself, so the rows below can't cover it (or catch its taps).
 */
function usePlace(anchor: RefObject<HTMLElement | null>): Pos | null {
  const [pos, setPos] = useState<Pos | null>(null)
  useLayoutEffect(() => {
    const place = () => {
      const el = anchor.current
      if (!el) return
      const r = el.getBoundingClientRect()
      const vv = window.visualViewport
      const top = vv?.offsetTop ?? 0
      const bottom = top + (vv?.height ?? window.innerHeight)
      const below = bottom - r.bottom - 12
      const above = r.top - top - 12
      const up = below < 200 && above > below
      const left = Math.max(8, Math.min(r.left, window.innerWidth - Math.min(320, window.innerWidth * 0.8) - 8))
      const maxHeight = Math.round(Math.max(96, Math.min(340, up ? above : below)))
      setPos(up ? { left, bottom: Math.round(window.innerHeight - r.top + 4), maxHeight } : { left, top: Math.round(r.bottom + 4), maxHeight })
    }
    place()
    const vv = window.visualViewport
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    vv?.addEventListener('resize', place)
    vv?.addEventListener('scroll', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
      vv?.removeEventListener('resize', place)
      vv?.removeEventListener('scroll', place)
    }
  }, [anchor])
  return pos
}

/** Fuzzy item picker opened by typing `[[` (SPEC §8.3), by the row it's typed in (`anchor`). */
export const LinkPicker = forwardRef<LinkPickerHandle, { query: string; excludeId?: string; anchor: RefObject<HTMLElement | null>; onPick: (id: string) => void; onClose: () => void }>(
  function LinkPicker({ query, excludeId, anchor, onPick, onClose }, ref) {
    const [sel, setSel] = useState(0)
    const results = useMemo(() => searchTitles(query, { limit: 6, exclude: excludeId ? new Set([excludeId]) : undefined }), [query, excludeId])
    const selected = Math.min(sel, Math.max(0, results.length - 1))
    const pos = usePlace(anchor)

    useImperativeHandle(ref, () => ({
      handleKey(e) {
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setSel((s) => Math.min(results.length - 1, s + 1))
          return true
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          setSel((s) => Math.max(0, s - 1))
          return true
        }
        if ((e.key === 'Enter' || e.key === 'Tab') && results[selected]) {
          e.preventDefault()
          onPick(results[selected]!)
          return true
        }
        if (e.key === 'Escape') {
          e.preventDefault()
          onClose()
          return true
        }
        return false
      },
    }))

    const over = (children: ReactNode, className: string, role?: string) =>
      pos &&
      createPortal(
        <div
          style={{ position: 'fixed', zIndex: 60, left: pos.left, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight }}
          className={className}
          role={role}
          aria-label={role ? 'Link to' : undefined}
        >
          {children}
        </div>,
        document.body,
      )
    if (!results.length) return over(<>No items match “{query}”</>, 'w-72 rounded-xl border border-line bg-surface p-3 text-sm text-muted shadow-lg')
    const idx = store.idx
    return over(
      results.map((id, i) => (
        <button
          type="button"
          key={id}
          role="option"
          aria-selected={i === selected}
          className={`block w-full px-3 py-2 text-left text-sm ${i === selected ? 'bg-surface-2' : ''}`}
          // Keep the caret in the row: mousedown, not pointerdown (preventing that makes WebKit drop the tap).
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onPick(id)}
        >
          <div className="truncate">{displayTitle(store.get(id)?.title ?? '', (x) => store.get(x)?.title) || 'Untitled'}</div>
          <div className="lab truncate text-faint">{breadcrumb(idx, id) || 'Top level'}</div>
        </button>
      )),
      'w-[min(20rem,80vw)] overflow-y-auto rounded-xl border border-line bg-surface py-1 shadow-lg',
      'listbox',
    )
  },
)
